import { useState, useEffect } from 'react'
import { Navigate, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Eye, EyeOff, Mail, AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Turnstile } from '@/components/Turnstile'
import { apiClient } from '@/lib/api'
import { SEOHead } from '@/components/SEOHead'

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [emailWarning, setEmailWarning] = useState<string | null>(null)
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [emailCheckResult, setEmailCheckResult] = useState<{
    is_temp: boolean
    score: number
    domain: string
  } | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>('')
  const [turnstileEnabled, setTurnstileEnabled] = useState(false)
  const [turnstileUnavailable, setTurnstileUnavailable] = useState(false)
  const [registrationEnabled, setRegistrationEnabled] = useState<boolean | null>(null)

  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Check if registration is enabled
  useEffect(() => {
    apiClient.getRegistrationEnabled()
      .then((data) => setRegistrationEnabled(data.registration_enabled))
      .catch(() => setRegistrationEnabled(true))  // Default to enabled if check fails
  }, [])

  // Fetch Turnstile site key
  useEffect(() => {
    apiClient.getTurnstileSiteKey()
      .then((data) => {
        if (data.enabled && data.site_key) {
          setTurnstileSiteKey(data.site_key)
          setTurnstileEnabled(true)
        }
      })
      .catch(() => {
        // Silently fail - Turnstile is optional
      })
  }, [])

  // If Turnstile widget errors or hangs (e.g. 300030), allow submit after timeout
  useEffect(() => {
    if (!turnstileEnabled || turnstileToken || turnstileUnavailable) return
    const t = setTimeout(() => setTurnstileUnavailable(true), 15000)
    return () => clearTimeout(t)
  }, [turnstileEnabled, turnstileToken, turnstileUnavailable])

  // Pre-fill form from invite link (admin invite_token or org invite_code)
  useEffect(() => {
    const inviteToken = searchParams.get('invite_token')
    const inviteCode = searchParams.get('invite_code')
    if (inviteToken) {
      apiClient.getInviteByToken(inviteToken)
        .then((inv: { email: string; first_name: string; last_name: string }) => {
          setFormData(prev => ({
            ...prev,
            email: inv.email || prev.email,
            firstName: inv.first_name || prev.firstName,
            lastName: inv.last_name || prev.lastName
          }))
        })
        .catch(() => {})
    } else if (inviteCode) {
      apiClient.getInviteByCode(inviteCode)
        .then((inv: { email: string }) => {
          setFormData(prev => ({ ...prev, email: inv.email || prev.email }))
        })
        .catch(() => {})
    }
  }, [searchParams])

  // Debounced email validation
  useEffect(() => {
    if (!formData.email || !formData.email.includes('@')) {
      setEmailWarning(null)
      setEmailCheckResult(null)
      return
    }

    const timer = setTimeout(async () => {
      setIsCheckingEmail(true)
      try {
        const result = await apiClient.checkEmailTemp(formData.email) as {
          is_temp: boolean
          score: number
          domain: string
        }
        setEmailCheckResult(result)
        
        if (result.is_temp) {
          setEmailWarning(
            `⚠️ This email appears to be from a temporary email service (score: ${result.score}/100). ` +
            `Please use a permanent email address to register.`
          )
        } else {
          setEmailWarning(null)
          if (result.score > 0) {
            setEmailWarning(
              `⚠️ This email domain has a risk score of ${result.score}/100. ` +
              `Consider using a more reputable email provider.`
            )
          }
        }
      } catch (err) {
        // Silently fail - don't block user if check fails
        setEmailWarning(null)
      } finally {
        setIsCheckingEmail(false)
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [formData.email])

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />
  }

  if (registrationEnabled === false) {
    return (
      <div className="min-h-screen bg-background dark flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Registration Disabled</CardTitle>
            <CardDescription className="text-muted-foreground">
              Registration is currently disabled. Please contact an administrator to create an account or enable registration.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Back to Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      setIsLoading(false)
      return
    }

    // Check if temp email was detected
    if (emailCheckResult?.is_temp) {
      setError(
        `Temporary or disposable email addresses are not allowed. ` +
        `Domain '${emailCheckResult.domain}' has a score of ${emailCheckResult.score}/100. ` +
        `Please use a permanent email address.`
      )
      setIsLoading(false)
      return
    }

    if (turnstileEnabled && turnstileSiteKey && !turnstileToken && !turnstileUnavailable) {
      const widgetContainer = document.querySelector('[data-turnstile-widget]')
      if (widgetContainer) {
        setError('Please complete the security verification')
        setIsLoading(false)
        return
      }
    }

    try {
      const inviteToken = searchParams.get('invite_token')
      const inviteCode = searchParams.get('invite_code')
      await apiClient.register({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        turnstile_token: turnstileToken || undefined,
        invite_token: inviteToken || undefined,
        invite_code: inviteCode || undefined
      })

      // Registration successful - redirect to verify email page
      navigate('/verify-email', {
        state: {
          email: formData.email,
          message: 'Registration successful! Please check your email to verify your account. After verification, an administrator will review and approve your access.'
        }
      })
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Registration failed. Please try again.'
      setError(errorMessage)
      // Reset Turnstile on error
      if (turnstileEnabled) {
        setTurnstileToken(null)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (field === 'email') {
      setError('') // Clear error when email changes
    }
  }

  return (
    <div className="min-h-screen bg-background dark flex items-center justify-center p-4 relative overflow-hidden">
      <SEOHead
        title="Create Account - Sycek OSINT Platform"
        description="Register for Sycek OSINT to access enterprise-grade cyber intelligence, threat monitoring, breach analysis, and OSINT investigation tools."
        canonical="/register"
      />
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent"></div>
      
      <Card className="relative z-10 w-full max-w-md border-border shadow-2xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold text-primary">Sycek OSINT</h1>
          </div>
          <CardTitle className="text-foreground">Create Account</CardTitle>
          <CardDescription className="text-muted-foreground">
            {(searchParams.get('invite_token') || searchParams.get('invite_code'))
              ? "You've been invited! Complete the form below to join."
              : 'Sign up to start using the OSINT investigation platform'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="firstName" className="text-sm font-medium text-foreground">
                  First Name
                </label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="lastName" className="text-sm font-medium text-foreground">
                  Last Name
                </label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
                {isCheckingEmail && (
                  <span className="text-xs text-muted-foreground">(checking...)</span>
                )}
              </label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                className={emailCheckResult?.is_temp ? 'border-destructive' : ''}
              />
              {emailCheckResult && (
                <div className={`flex items-start gap-2 text-xs p-2 rounded-md ${
                  emailCheckResult.is_temp 
                    ? 'bg-destructive/10 text-destructive border border-destructive/20' 
                    : emailCheckResult.score > 50
                    ? 'bg-yellow-500/10 text-yellow-600 border border-yellow-500/20'
                    : 'bg-green-500/10 text-green-600 border border-green-500/20'
                }`}>
                  {emailCheckResult.is_temp ? (
                    <>
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Temporary email detected (Score: {emailCheckResult.score}/100). 
                        This email cannot be used for registration.
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Email validation: {emailCheckResult.score}/100 
                        {emailCheckResult.score > 50 && ' - Low risk domain'}
                      </span>
                    </>
                  )}
                </div>
              )}
              {emailWarning && !emailCheckResult?.is_temp && (
                <div className="text-xs text-yellow-600 bg-yellow-500/10 border border-yellow-500/20 rounded-md p-2">
                  {emailWarning}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-foreground">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
                Confirm Password
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <div className="text-xs text-destructive">
                  Passwords do not match
                </div>
              )}
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {turnstileEnabled && (
              <>
                <Turnstile
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => { setTurnstileToken(token); setTurnstileUnavailable(false) }}
                  onError={() => { setTurnstileToken(null); setTurnstileUnavailable(true) }}
                  onExpire={() => setTurnstileToken(null)}
                  theme="auto"
                  size="normal"
                  className="my-4"
                />
                {turnstileUnavailable && (
                  <p className="text-sm text-amber-500 mt-2">
                    Verification couldn&apos;t load. You can try registering anyway.
                  </p>
                )}
              </>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={
                isLoading ||
                (emailCheckResult?.is_temp === true) ||
                (turnstileEnabled && !turnstileToken && !turnstileUnavailable)
              }
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-primary hover:underline">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

